import {
  AccountBalance as AccountBalanceIcon,
  AccountCircle as AccountCircleIcon,
  Business as BusinessIcon,
  Construction as ConstructionIcon,
  CreditCard as CreditCardIcon,
  MonitorHeart as MonitorHeartIcon,
  Redeem as RedeemIcon,
} from '@mui/icons-material';
import { Divider, List, ListItemButton, ListItemIcon, ListItemText, useTheme } from '@mui/material';
import { forwardRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { config } from 'appConfig';
import { useExtendedIntl } from 'hooks/useExtendedIntl';
import { TeamsMenu } from '../TeamsMenu/TeamsMenu';
import { APIPermission } from 'typeDeclarations/enums';
import { FeatureFlagsList } from 'featureFlags/FeatureFlagsList';
import { DefaultDialog } from 'shared/DefaultDialog/DefaultDialog';
import { PermissionsViewDecider } from 'shared/PermissionsViewDecider';
import { AccountArrowIconRight, BullhornIcon } from 'components/Icons/index';
import { NavigationMenuHeader } from './NavigationMenuHeader/NavigationMenuHeader';

interface NavigationMenuProps {
  onLinkClick: () => void;
  onMoreTeamsClick: () => void;
  onTeamClick: (teamId: string) => void;
  onAddMoneyClick: () => void;
}

const dividerSxProps = {
  opacity: 0.6,
  margin: '0.5rem 1rem',
};

export const NavigationMenu = forwardRef<HTMLElement, NavigationMenuProps>(
  ({ onLinkClick, onMoreTeamsClick, onAddMoneyClick, onTeamClick }, ref) => {
    const theme = useTheme();
    const { formatMessage } = useExtendedIntl();
    const [isFeatureFlagsDialogOpen, setIsFeatureFlagsDialogOpen] = useState(false);

    return (
      <>
        <List component="nav" ref={ref} sx={{ width: '18rem', minHeight: '30rem' }}>
          {/* FIXME change name to reflect re-rendering and not closing */}
          <NavigationMenuHeader onLinkClick={onLinkClick} onAddMoneyClick={onAddMoneyClick} />
          {/* Pulse Check */}
          <ListItemButton to="/pulse-check" component={Link} onClick={onLinkClick} data-testid="menu-pulse-check">
            <ListItemIcon>
              <MonitorHeartIcon htmlColor={theme.palette.text.primary} />
            </ListItemIcon>
            <ListItemText>{formatMessage({ id: 'pulse-check.page-title' })}</ListItemText>
          </ListItemButton>
          {/* Businesses */}
          <ListItemButton to="/businesses" component={Link} onClick={onLinkClick} data-testid="menu-businesses">
            <ListItemIcon>
              <BusinessIcon htmlColor={theme.palette.text.primary} />
            </ListItemIcon>
            <ListItemText>{formatMessage({ id: 'shared.advertiser' }, { plural: true })}</ListItemText>
          </ListItemButton>
          {/* Campaigns */}
          <ListItemButton to="/campaigns" component={Link} onClick={onLinkClick} data-testid="menu-campaigns">
            <ListItemIcon>
              <BullhornIcon htmlColor={theme.palette.text.primary} />
            </ListItemIcon>
            <ListItemText>{formatMessage({ id: 'shared.campaign' }, { plural: true })}</ListItemText>
          </ListItemButton>
          {/* Teams */}
          <TeamsMenu onMoreTeamsClick={onMoreTeamsClick} onTeamClick={onTeamClick} />
          <Divider sx={dividerSxProps} />
          <PermissionsViewDecider requiredPermissions={[APIPermission.EstimatedCostPerGoalWithUrl]}>
            <ListItemButton
              component={Link}
              to="/budget-estimator"
              onClick={onLinkClick}
              data-testid="menu-budget-estimator"
            >
              <ListItemIcon>
                <AccountBalanceIcon htmlColor={theme.palette.text.primary} />
              </ListItemIcon>
              <ListItemText>{formatMessage({ id: 'navigation-menu.budget-estimator' })}</ListItemText>
            </ListItemButton>
          </PermissionsViewDecider>
          <ListItemButton onClick={() => setIsFeatureFlagsDialogOpen(true)} data-testid="menu-beta-features">
            <ListItemIcon>
              <ConstructionIcon htmlColor={theme.palette.text.primary} />
            </ListItemIcon>
            <ListItemText>{formatMessage({ id: 'feature-flags.beta-features' })}</ListItemText>
          </ListItemButton>
          <Divider sx={dividerSxProps} />
          <ListItemButton
            component={Link}
            onClick={onLinkClick}
            to="/account-settings/invite-a-friend"
            data-testid="menu-invite-friends"
          >
            <ListItemIcon>
              <RedeemIcon color="secondary" />
            </ListItemIcon>
            <ListItemText>{formatMessage({ id: 'shared.invite-friends' })}</ListItemText>
          </ListItemButton>
          <ListItemButton component={Link} to="/team/billing" onClick={onLinkClick} data-testid="menu-billing">
            <ListItemIcon>
              <CreditCardIcon htmlColor={theme.palette.text.primary} />
            </ListItemIcon>
            <ListItemText>{formatMessage({ id: 'navigation-menu.team-management' })}</ListItemText>
          </ListItemButton>
          <ListItemButton
            component={Link}
            to="/account-settings"
            onClick={onLinkClick}
            data-testid="menu-account-settings"
          >
            <ListItemIcon>
              <AccountCircleIcon htmlColor={theme.palette.text.primary} />
            </ListItemIcon>
            <ListItemText>{formatMessage({ id: 'navigation-menu.account-settings' })}</ListItemText>
          </ListItemButton>
          <Divider sx={dividerSxProps} />
          <ListItemButton component={Link} to={config.routes.logout} data-testid="menu-logout">
            <ListItemIcon>
              <AccountArrowIconRight htmlColor={theme.palette.text.primary} />
            </ListItemIcon>
            <ListItemText>{formatMessage({ id: 'navigation-menu.log-out' })}</ListItemText>
          </ListItemButton>
        </List>
        <DefaultDialog
          content={<FeatureFlagsList />}
          open={isFeatureFlagsDialogOpen}
          onClose={() => setIsFeatureFlagsDialogOpen(false)}
          title={formatMessage({ id: 'feature-flags.beta-features' })}
          description={formatMessage({ id: 'feature-flags.description' })}
        />
      </>
    );
  },
);
